// pages/ContactUs.jsx
import React from "react";
import { FiMapPin, FiPhone, FiMail, FiExternalLink, FiSend } from "react-icons/fi";
import { useSEO } from "../../hooks/useSEO";
import Layout from "../../components/Layout";
import PageHeader from "../../components/ui/PageHeader";
import { useTheme } from "../../theme/ThemeProvider";
import { CONTACT_ROUTE } from "../../routes/constant";

const ContactUs = () => {
  const { isDarkMode } = useTheme();

  const breadcrumbItems = [
    {
      label: "Home",
      path: "/",
      icon: true,
    },
  ];

  const currentPage = {
    label: "Contact Us",
    path: "/contact-us",
  };

  // Use SEO hook
  useSEO(CONTACT_ROUTE);

  // JSON defined contact data
  const contactData = {
    companyInfo: {
      name: "Selltech E-Waste Management Solution Pvt Ltd.",
      address: "Bhapra Road, Samalkha, Haryana-132101",
      cin: "U62099HR2024PTC123824",
      supportNumber: "7404252555",
      email: "admin@tap2cash.in",
      careEmail: "care@tap2cash.in"
    },
    grievanceOfficer: {
      name: "Abhishek Punia",
      email: "care@tap2cash.in"
    },
    contactCards: [
      {
        id: 1,
        title: "Visit Us",
        icon: "FiMapPin",
        content: "Bhapra Road, Samalkha, Haryana-132101",
        subContent: "Selltech E-Waste Management Solution Pvt Ltd.",
        link: "https://maps.google.com/?q=Bhapra+Road+Samalkha+Haryana+132101",
        action: "Get Directions"
      },
      {
        id: 2,
        title: "Call Us",
        icon: "FiPhone",
        content: "7404252555",
        subContent: "Support: 7404252555",
        link: "tel:7404252555",
        action: "Call Now"
      },
      {
        id: 3,
        title: "Email Us",
        icon: "FiMail",
        content: "admin@tap2cash.in",
        subContent: "CIN: U62099HR2024PTC123824",
        link: "mailto:admin@tap2cash.in",
        action: "Send Email"
      }
    ],
    quickActions: [
      {
        id: 1,
        label: "Email Us",
        link: "mailto:admin@tap2cash.in",
        icon: "FiMail",
        color: "green"
      },
      {
        id: 2,
        label: "Call Support",
        link: "tel:7404252555",
        icon: "FiPhone",
        color: "blue"
      }
    ],
    mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.3871536142437!2d77.03312397528268!3d28.467885075755092!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d19209ea91c57%3A0xe6cc4d511b74c122!2sTap2Cash!5e0!3m2!1sen!2sin!4v1772947151199!5m2!1sen!2sin"
  };

  // Icon mapping
  const getIcon = (iconName, className = "w-4 h-4") => {
    const icons = {
      FiMapPin: <FiMapPin className={className} />,
      FiPhone: <FiPhone className={className} />,
      FiMail: <FiMail className={className} />,
      FiExternalLink: <FiExternalLink className={className} />,
      FiSend: <FiSend className={className} />
    };
    return icons[iconName] || null;
  };

  return (
    <Layout>
      <PageHeader
        title="Contact Us"
        breadcrumbItems={breadcrumbItems}
        currentPage={currentPage}
      />

      {/* Main Content */}
      <div
        className={`transition-colors duration-200 ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Contact Info Cards - Compact Grid */}
          <div className="grid grid-cols-1 gap-3 mb-6 sm:grid-cols-2 lg:grid-cols-3">
            {contactData.contactCards.map((card) => (
              <a
                key={card.id}
                href={card.link}
                target={card.link.startsWith("http") ? "_blank" : undefined}
                rel={card.link.startsWith("http") ? "noopener noreferrer" : undefined}
                className={`group p-3 rounded-lg transition-all duration-200 ${
                  isDarkMode
                    ? "bg-gray-800 hover:bg-gray-750"
                    : "bg-white hover:bg-green-50 border border-gray-100 hover:border-green-200"
                }`}
              >
                <div className="flex items-start space-x-3 rtl:space-x-reverse">
                  <div
                    className={`p-1.5 rounded-lg ${
                      isDarkMode
                        ? "bg-green-900/30 text-green-400"
                        : "bg-green-100 text-green-600"
                    }`}
                  >
                    {getIcon(card.icon, "w-3.5 h-3.5")}
                  </div>
                  <div className="flex-1">
                    <h3
                      className={`text-xs font-medium ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {card.title}
                    </h3>
                    <p
                      className={`text-sm font-semibold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {card.content}
                    </p>
                    <p
                      className={`text-[10px] mt-0.5 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {card.subContent}
                    </p>
                    <span
                      className={`inline-block mt-1.5 text-[10px] font-medium ${
                        isDarkMode
                          ? "text-green-400 group-hover:text-green-300"
                          : "text-green-600 group-hover:text-green-700"
                      }`}
                    >
                      {card.action} →
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Split Grid: Contact Info (Left) & Map (Right) */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Left Column - Detailed Contact Info */}
            <div className="space-y-3">
              {/* Company Info Card */}
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-white border border-gray-100"
                }`}
              >
                <h2
                  className={`text-sm font-bold mb-2 flex items-center ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  <span
                    className={`w-1 h-4 rounded-full mr-1.5 ${
                      isDarkMode ? "bg-green-400" : "bg-green-600"
                    }`}
                  ></span>
                  Company Details
                </h2>
                <div className="space-y-1.5 text-xs">
                  <p className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                    <span className="font-semibold">{contactData.companyInfo.name}</span>
                  </p>
                  <p
                    className={`flex items-start ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    <FiMapPin className="w-3 h-3 mr-1.5 mt-0.5 flex-shrink-0" />
                    <span className="text-[11px]">{contactData.companyInfo.address}</span>
                  </p>
                  <p
                    className={`flex items-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    <FiPhone className="w-3 h-3 mr-1.5" />
                    <a
                      href={`tel:${contactData.companyInfo.supportNumber}`}
                      className={`text-[11px] hover:underline ${
                        isDarkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                    >
                      Support: {contactData.companyInfo.supportNumber}
                    </a>
                  </p>
                  <p
                    className={`flex items-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    <FiMail className="w-3 h-3 mr-1.5" />
                    <a
                      href={`mailto:${contactData.companyInfo.email}`}
                      className={`text-[11px] hover:underline ${
                        isDarkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                    >
                      {contactData.companyInfo.email}
                    </a>
                  </p>
                  <p className={`text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    CIN: {contactData.companyInfo.cin}
                  </p>
                </div>
              </div>

              {/* Grievance Officer Card */}
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-white border border-gray-100"
                }`}
              >
                <h2
                  className={`text-sm font-bold mb-2 flex items-center ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  <span
                    className={`w-1 h-4 rounded-full mr-1.5 ${
                      isDarkMode ? "bg-green-400" : "bg-green-600"
                    }`}
                  ></span>
                  Grievance Officer
                </h2>
                <div className="space-y-1.5">
                  <div
                    className={`flex items-center justify-between p-1.5 rounded ${
                      isDarkMode ? "bg-gray-700/50" : "bg-gray-50"
                    }`}
                  >
                    <span className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                      Name
                    </span>
                    <span className={`text-[11px] font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                      {contactData.grievanceOfficer.name}
                    </span>
                  </div>
                  <div
                    className={`flex items-center justify-between p-1.5 rounded ${
                      isDarkMode ? "bg-gray-700/50" : "bg-gray-50"
                    }`}
                  >
                    <span className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                      Email
                    </span>
                    <a
                      href={`mailto:${contactData.grievanceOfficer.email}`}
                      className={`text-[11px] font-medium hover:underline ${
                        isDarkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                    >
                      {contactData.grievanceOfficer.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-white border border-gray-100"
                }`}
              >
                <h2
                  className={`text-sm font-bold mb-2 flex items-center ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  <span
                    className={`w-1 h-4 rounded-full mr-1.5 ${
                      isDarkMode ? "bg-green-400" : "bg-green-600"
                    }`}
                  ></span>
                  Quick Actions
                </h2>
                <div className="grid grid-cols-2 gap-2">
                  {contactData.quickActions.map((action) => (
                    <a
                      key={action.id}
                      href={action.link}
                      className={`py-1.5 px-2 text-[11px] text-center rounded-md transition-colors flex items-center justify-center gap-1 ${
                        action.color === "green"
                          ? isDarkMode
                            ? "bg-green-900/30 text-green-400 hover:bg-green-900/50"
                            : "bg-green-100 text-green-700 hover:bg-green-200"
                          : isDarkMode
                            ? "bg-blue-900/30 text-blue-400 hover:bg-blue-900/50"
                            : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      }`}
                    >
                      {getIcon(action.icon, "w-3 h-3")}
                      {action.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Map */}
            <div
              className={`rounded-lg overflow-hidden shadow-lg h-full min-h-[400px] ${
                isDarkMode ? "border border-gray-700" : "border border-gray-200"
              }`}
            >
              <iframe
                src={contactData.mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "400px" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Tap2Cash Location"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>

          {/* Trademark Notice - Compact */}
          <div
            className={`mt-5 text-[10px] text-center ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            <p>
              ** All trademarks, logos, product and brand names belong to their respective owners.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactUs;